import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AlertProvider } from './context/alertContext';
import { trackPageView } from './analytics/googleAnalytics';
import Header from './components/Header';
import HomePage from './components/HomePage';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import RouteWrapper from './components/RouteWrapper';
import DecorativeShapes from './components/DecorativeShapes';
import './styles/styles.css';

// Lazy load non-critical components
const AboutMe = lazy(() => import('./components/AboutMe'));
const Courses = lazy(() => import('./components/Courses'));
const CourseDetailPage = lazy(() => import('./components/CourseDetailPage'));
const Products = lazy(() => import('./components/Products'));
const FAQ = lazy(() => import('./components/FAQ'));
const Payment = lazy(() => import('./components/Payment'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const Contact = lazy(() => import('./components/Contact'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./components/TermsConditions'));
const NotFound = lazy(() => import('./components/NotFound'));
const ShapesTestPage = lazy(() => import('./components/ShapesTestPage'));

// Component to track page views with Google Analytics
function RouteAnalytics() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '60vh',
    fontSize: '1.2rem',
    color: 'var(--primary-color)'
  }}>
    Loading...
  </div>
);

function App() {
  return (
    <AlertProvider>
      <Router>
        <ScrollToTop />
        <RouteAnalytics />
        <div className="App">
          <DecorativeShapes />
          <Header />
          <main>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<RouteWrapper><HomePage /></RouteWrapper>} />
                {/* Redirect /home to / */}
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="/about" element={<RouteWrapper><AboutMe /></RouteWrapper>} />
                <Route path="/courses" element={<RouteWrapper><Courses /></RouteWrapper>} />
                <Route path="/courses/:courseId" element={<RouteWrapper><CourseDetailPage /></RouteWrapper>} />
                <Route path="/courses/:courseId/payment" element={<RouteWrapper><Payment /></RouteWrapper>} />
                <Route path="/products" element={<RouteWrapper><Products /></RouteWrapper>} />
                <Route path="/products/:productId/payment" element={<RouteWrapper><Payment /></RouteWrapper>} />
                <Route path="/payment-success" element={<RouteWrapper><PaymentSuccess /></RouteWrapper>} />
                {/* <Route path="/resources" element={<Resources />} /> */}
                {/* <Route path="/culture" element={<KoreanCulture />} /> */}
                {/* <Route path="/travel" element={<Travel />} /> */}
                {/* <Route path="/blog" element={<BlogPosts />} /> */}
                <Route path="/faq" element={<RouteWrapper><FAQ /></RouteWrapper>} />
                <Route path="/payment" element={<Navigate to="/courses" replace />} />
                <Route path="/contact" element={<RouteWrapper><Contact /></RouteWrapper>} />
                <Route path="/shapes-test" element={<RouteWrapper><ShapesTestPage /></RouteWrapper>} />
                <Route path="/privacy-policy" element={<RouteWrapper><PrivacyPolicy /></RouteWrapper>} />
                <Route path="/terms-conditions" element={<RouteWrapper><TermsConditions /></RouteWrapper>} />
                {/* Catch-all route for 404 errors */}
                <Route path="*" element={<RouteWrapper><NotFound /></RouteWrapper>} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </Router>
    </AlertProvider>
  );
}export default App;
